import {Parameter, ParameterStore} from '@/Core/mobx/ParameterStore/ParameterStore';
import {Box, Button, Card, Grid} from '@material-ui/core';
import CheckIcon from '@material-ui/icons/Check';
import {observer} from 'mobx-react-lite';
import AgentAssistPanel from '../Assist/AgentAssistPanel';
import AgentInfo from './AgentInfo';
import BasicInformation from './BasicInformation';
import PolicyInformation from '../PreApprovals/Components/PolicyInformation';
import ConditionalCoverageBanking from '../PreApprovals/Components/CoverageBanking';
import CarrierPortalInformation from './CarrierPortalInformation';
import {useEffect, CSSProperties, useRef, useState, useMemo} from 'react';
import AthenaAPI from '@/APIs/AthenaAPI';
import RootStore from '@/Core/mobx/RootStore';
import {User} from '@/Auth/User';
import {IS_PRODUCTION} from '@/constants';

type ValueChanged = (p?: Parameter, value?: string | number | boolean | undefined) => void;

const getSubmitButtonStyle = (disabled: boolean): CSSProperties => ({
    marginTop: 16,
    backgroundColor: disabled ? '#B8C9C7' : '#456D69',
    color: 'white',
    fontSize: 14,
    fontWeight: 500,
    fontFamily: 'FredokaRegular',
    textTransform: 'none',
});

interface PostAppWrapPanelProps {
    parameterStore: ParameterStore;
    valueChanged: ValueChanged;
}

export const PostAppWrapPanel = observer(({parameterStore, valueChanged}: PostAppWrapPanelProps) => {
    const {contactsFormStore} = RootStore.agentPortalStore;
    const {approval, prequal} = parameterStore || {};
    const [assistPanelExpanded, setAssistPanelExpanded] = useState(true);
    const [agentInfoCompleted, setAgentInfoCompleted] = useState(false);
    const [basicInformationCompleted, setBasicInformationCompleted] = useState(false);
    const [policyInformationCompleted, setPolicyInformationCompleted] = useState(false);
    const [conditionalCoverageBankingCompleted, setConditionalCoverageBankingCompleted] = useState(false);
    const [carrierPortalInformationCompleted, setCarrierPortalInformationCompleted] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submittingRef = useRef(false);
    const warningOffset =
        (!IS_PRODUCTION ? 76 : 0) +
        (approval?.state?.value != null && !RootStore.currentAgentStates.includes(approval.state.value) ? 80 : 0);
    const gridContainerStyle = useMemo(
        () => ({height: `calc(100vh - ${120 + warningOffset}px)`, overflowY: 'hidden' as const}),
        [warningOffset],
    );

    // Fetch recommended products if not already loaded
    useEffect(() => {
        const fetchProductsIfNeeded = async () => {
            // Check if products are not loaded
            if (
                !parameterStore.recommendedProductsResponse ||
                !parameterStore.recommendedProductsResponse.allProducts
            ) {
                try {
                    await parameterStore.fetchRecommendedProducts();
                } catch (error) {
                    console.error('Failed to fetch recommended products:', error);
                }
            }
        };

        fetchProductsIfNeeded();
    }, [parameterStore]);

    const handleSubmitToCaseManagement = async () => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setIsSubmitting(true);
        try {
            const customerEmail = approval?.emailAddress?.value || '';
            const formLink = window.location.href;
            const response = await AthenaAPI.createPreapprovalTask(customerEmail, formLink);
            if (response && response.success) {
                parameterStore.approval.agentEmail.setValue(User.Info.email);
                await contactsFormStore.saveForm();
                RootStore.uiStore.showSnackbar('Preapproval task created successfully', 'success');
            } else {
                RootStore.uiStore.showSnackbar('Failed to create preapproval task', 'error');
            }
        } catch (error) {
            console.error('Error creating preapproval task', error);
            RootStore.uiStore.showSnackbar('Failed to create preapproval task', 'error');
        } finally {
            submittingRef.current = false;
            setIsSubmitting(false);
        }
    };

    return (
        <Grid container spacing={2} style={gridContainerStyle}>
            <Grid
                item
                xs={assistPanelExpanded ? 8 : 11}
                style={{height: '100%', overflowY: 'auto', paddingTop: 40, paddingBottom: 30}}
            >
                <Card>
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={12} md={12}>
                            <AgentInfo
                                valueChanged={valueChanged}
                                parameters={approval}
                                setCategory={() => {}}
                                index={1}
                                setCompleted={setAgentInfoCompleted}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <BasicInformation
                                valueChanged={valueChanged}
                                parameters={approval}
                                setCategory={() => {}}
                                index={2}
                                setCompleted={setBasicInformationCompleted}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <PolicyInformation
                                valueChanged={valueChanged}
                                parameters={approval}
                                setCategory={() => {}}
                                index={3}
                                setCompleted={setPolicyInformationCompleted}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <ConditionalCoverageBanking
                                valueChanged={valueChanged}
                                parameters={approval}
                                setCategory={() => {}}
                                index={4}
                                setCompleted={setConditionalCoverageBankingCompleted}
                            />
                        </Grid>
                        <Grid item xs={12} sm={12} md={12}>
                            <CarrierPortalInformation
                                valueChanged={valueChanged}
                                parameters={approval}
                                setCategory={() => {}}
                                index={5}
                                setCompleted={setCarrierPortalInformationCompleted}
                            />
                        </Grid>
                    </Grid>
                </Card>
            </Grid>
            <Grid
                item
                xs={assistPanelExpanded ? 4 : 1}
                style={{
                    height: '100%',
                    overflowY: 'auto',
                    paddingTop: 16,
                    ...(!assistPanelExpanded && {display: 'flex', justifyContent: 'center'}),
                }}
            >
                <AgentAssistPanel
                    tab="post-app-wrap"
                    doOnCollapse={() => setAssistPanelExpanded(false)}
                    doOnExpand={() => setAssistPanelExpanded(true)}
                    onValueChangedNotes={(value) => valueChanged({...prequal?.notes, name: 'notes'}, value)}
                    nestPostAppChecklist={
                        <Box>
                            {[
                                {name: 'Agent Info', completed: agentInfoCompleted},
                                {name: 'Basic Information', completed: basicInformationCompleted},
                                {name: 'Policy Information', completed: policyInformationCompleted},
                                {
                                    name: 'Conditional Coverage Banking',
                                    completed: conditionalCoverageBankingCompleted,
                                },
                                {
                                    name: 'Carrier Portal Information',
                                    completed: carrierPortalInformationCompleted,
                                },
                            ].map((section) => (
                                <Box
                                    key={section.name}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        marginBottom: 8,
                                        color: section.completed ? '#456D69' : '#838383',
                                    }}
                                >
                                    <Box style={{width: 24, marginRight: 8}}>
                                        {section.completed && <CheckIcon style={{fontSize: 18, color: '#456D69'}} />}
                                    </Box>
                                    <span>{section.name}</span>
                                </Box>
                            ))}
                            {(() => {
                                const isIncomplete =
                                    !agentInfoCompleted ||
                                    !basicInformationCompleted ||
                                    !policyInformationCompleted ||
                                    !conditionalCoverageBankingCompleted ||
                                    !carrierPortalInformationCompleted;
                                const isDisabled = isIncomplete || isSubmitting;
                                return (
                                    <Button
                                        variant="contained"
                                        fullWidth
                                        disabled={isDisabled}
                                        style={getSubmitButtonStyle(isDisabled)}
                                        onClick={handleSubmitToCaseManagement}
                                    >
                                        SUBMIT TO CASE MANAGEMENT
                                    </Button>
                                );
                            })()}
                        </Box>
                    }
                />
            </Grid>
        </Grid>
    );
});
